import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, ChevronDown } from 'lucide-react'

export type DropdownOption<T extends string> = { value: T; label: ReactNode }

/**
 * A select with our own list: the chosen option shows a check, the list
 * can carry logos, and it closes on a click elsewhere or Escape (the same
 * pattern as the notification bell and the account menu).
 */
export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  label,
  className = '',
  disabled = false,
}: {
  value: T
  options: DropdownOption<T>[]
  onChange: (value: T) => void
  /** what the control is, for screen readers */
  label: string
  className?: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const current = options.find((o) => o.value === value) ?? options[0]
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (root.current && !root.current.contains(event.target as Node)) setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div ref={root} className={`relative ${className}`}>
      <button
        type="button"
        className="control flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="flex min-w-0 items-center gap-2 truncate">{current?.label}</span>
        <ChevronDown className={`size-4 shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <ul role="listbox" aria-label={label} className="card absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-auto p-1.5 shadow-lg shadow-black/40">
          {options.map((option) => {
            const selected = option.value === value
            return (
              <li key={option.value} role="option" aria-selected={selected}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-[8px] px-3 py-2.5 text-left text-sm hover:bg-surface-2"
                  onClick={() => {
                    onChange(option.value)
                    setOpen(false)
                  }}
                >
                  <span className="flex min-w-0 items-center gap-2 truncate">{option.label}</span>
                  {selected ? <Check className="size-4 shrink-0 text-accent" /> : null}
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}
